import type { ExpectedResponse } from '../../sdk/content';
import type { SemanticActionKind } from '../../sdk/input';
import type { PlayerId } from '../../shared/types/ids';
import type { BodyZone, ThriveInputEvent } from '../../shared/types/input-events';
import { BODY_ZONES } from '../../shared/types/input-events';
import type { GameplayTunables } from '../../shared/types/tunables';

/**
 * Mode-keyed response matchers.
 *
 * Deciding whether an answer is right belongs to the platform, never to a scene (D-034 PS-3).
 * That is what lets keyboard and camera share one logic path, and what will let the cooperative
 * manager resolve two children against one expectation in Phase 2 without extraction surgery.
 *
 * A matcher is deliberately *not* a timer. It reads one semantic event and returns a verdict;
 * the runtime owns every clock in the system, so dwell and settle windows are testable without
 * real time and a paused mission genuinely stops.
 */

export type MatcherVerdict =
  /** Nothing about this event speaks to the question being asked. */
  | { kind: 'IGNORED' }
  /**
   * A candidate answer that must hold. The runtime commits it after `commitAfterMs` unless a
   * later event from *the same player* re-arms, or the round ends first — the child stepping
   * through the wrong portal on the way to the right one is not an answer.
   *
   * `playerId` is what makes the candidate one child's rather than the round's (D-045 PS2-2): the
   * runtime keeps a slot per player, so two children answering at once cannot cancel each other.
   */
  | { kind: 'ARMED'; playerId: PlayerId; correct: boolean; commitAfterMs: number }
  /** An answer with nothing to wait for. Also one child's, never the team's. */
  | { kind: 'RESOLVED'; playerId: PlayerId; correct: boolean };

export interface ResponseMatcher {
  readonly mode: ExpectedResponse['mode'];
  observe(event: ThriveInputEvent): MatcherVerdict;
  /**
   * Forgets what this one player has done so far, after their answer has been committed.
   *
   * The round's listening outlives a wrong answer in cooperative play (D-045 PS2-1), so the
   * matcher outlives it too — and a child starting their next try must start from zero without
   * touching the other child's count.
   */
  reset(playerId: PlayerId): void;
  /**
   * Undoes the single most recent observation by this player (S2-4).
   *
   * Not the same thing as {@link ResponseMatcher.reset}, and the difference is the whole point. A
   * reset forgets everything a child has done; this forgets exactly the onset that armed the
   * candidate the runtime has just discarded, and leaves every earlier one credited. Without it a
   * counting round is unwinnable after an interruption: the final jump *is* the candidate, so
   * discarding the candidate while keeping its count leaves the child at the target with nothing to
   * show for it, and the repeat they naturally make overshoots into a wrong answer.
   *
   * Optional: a matcher that keeps no history has nothing to undo.
   */
  rollbackLastObservation?(playerId: PlayerId): void;
}

export interface ResponseMatcherOptions {
  expected: ExpectedResponse;
  tunables: GameplayTunables;
}

export class UnsupportedResponseModeError extends Error {
  readonly mode: string;

  constructor(mode: string, detail: string) {
    super(`No Phase 1 matcher can resolve a "${mode}" expectation: ${detail}`);
    this.name = 'UnsupportedResponseModeError';
    this.mode = mode;
  }
}

/**
 * The onset event for each action.
 *
 * `STAND` and `LOWER_HANDS` are absent on purpose. They are *retractions*: the keyboard adapter
 * emits them on blur, tab-hide, freeze and a mechanic switch, with no child action behind them
 * (D-035), and a pose detector will do the same when a child walks out of frame. Nothing that can
 * be produced by a child leaving may ever answer a question.
 */
const ONSET_EVENT_FOR_ACTION: Partial<Record<SemanticActionKind, ThriveInputEvent['type']>> = {
  ENTER_ZONE: 'PLAYER_ZONE_CHANGED',
  JUMP: 'PLAYER_JUMPED',
  CROUCH: 'PLAYER_CROUCHED',
  RAISE_HANDS: 'PLAYER_RAISED_HANDS',
  BE_STILL: 'PLAYER_BECAME_STILL',
  MOVE: 'PLAYER_MOVED',
};

/**
 * Every input event that is a child *doing* something (R1-12).
 *
 * The complement — `PLAYER_STOOD`, `PLAYER_LOWERED_HANDS`, and the two frame-presence events —
 * is what arrives when a child stops, walks away, or when the browser blurs the tab. Exported so
 * the runtime's narrative beat obeys the same onset rule the matchers do, rather than keeping a
 * second, drifting list of "things that count".
 */
const ONSET_EVENT_TYPES: ReadonlySet<ThriveInputEvent['type']> = new Set(
  Object.values(ONSET_EVENT_FOR_ACTION).filter(
    (type): type is ThriveInputEvent['type'] => type !== undefined,
  ),
);

export function isOnsetInputEvent(event: ThriveInputEvent): boolean {
  return ONSET_EVENT_TYPES.has(event.type);
}

/**
 * The two postures a yes/no mechanic uses (Spider-Sense Factory: hands up for yes, crouch for
 * no). When one of them is the answer, the other is a wrong answer rather than an irrelevant
 * movement — everything else the child does is neither.
 */
const BINARY_POSTURES: readonly SemanticActionKind[] = ['RAISE_HANDS', 'CROUCH'];

interface ActionExpectation {
  kind: SemanticActionKind;
  zone?: BodyZone;
  count?: number;
}

function isBodyZone(value: unknown): value is BodyZone {
  return typeof value === 'string' && (BODY_ZONES as readonly string[]).includes(value);
}

/** Accepts `'center'` and `{ zone: 'center' }` — both spellings appear in authored content. */
function zoneOf(value: unknown): BodyZone | null {
  if (isBodyZone(value)) return value;
  if (typeof value === 'object' && value !== null) {
    const zone = (value as { zone?: unknown }).zone;
    if (isBodyZone(zone)) return zone;
  }
  return null;
}

function actionOf(value: unknown): ActionExpectation | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as { kind?: unknown; zone?: unknown; count?: unknown };
  if (typeof record.kind !== 'string') return null;
  if (ONSET_EVENT_FOR_ACTION[record.kind as SemanticActionKind] === undefined) return null;

  const expectation: ActionExpectation = { kind: record.kind as SemanticActionKind };
  const zone = zoneOf(record.zone);
  if (zone !== null) expectation.zone = zone;
  if (typeof record.count === 'number' && Number.isInteger(record.count) && record.count > 0) {
    expectation.count = record.count;
  }
  return expectation;
}

/**
 * Whether the runtime can resolve this expectation at all.
 *
 * Used before a round is presented: a question nobody can answer would strand a child in front of
 * a prompt with no way out, so the runtime skips it rather than running it.
 */
export function isSupportedResponseMode(expected: ExpectedResponse): boolean {
  switch (expected.mode) {
    case 'BODY_ZONE':
      return zoneOf(expected.value) !== null;
    case 'ACTION_COUNT': {
      const action = actionOf(expected.value);
      return action !== null && action.count !== undefined;
    }
    case 'SEMANTIC_ACTION': {
      const action = actionOf(expected.value);
      if (action === null) return false;
      return action.kind !== 'ENTER_ZONE' || action.zone !== undefined;
    }
    default:
      return false;
  }
}

function createZoneMatcher(zone: BodyZone, dwellMs: number): ResponseMatcher {
  return {
    mode: 'BODY_ZONE',
    observe(event: ThriveInputEvent): MatcherVerdict {
      if (event.type !== 'PLAYER_ZONE_CHANGED') return { kind: 'IGNORED' };
      // Standing in a portal is not choosing it. The dwell is what separates "walked past" from
      // "meant it", and it is the same window the camera path will use (GDS §14).
      return {
        kind: 'ARMED',
        playerId: event.playerId,
        correct: event.zone === zone,
        commitAfterMs: dwellMs,
      };
    },
    // Where a child is standing is not history the matcher keeps; the next zone event says it all.
    reset(): void {},
  };
}

function createCountMatcher(
  action: Required<Pick<ActionExpectation, 'kind' | 'count'>>,
  options: ResponseMatcherOptions,
): ResponseMatcher {
  const onset = ONSET_EVENT_FOR_ACTION[action.kind];
  /**
   * What this matcher has actually been shown, per player.
   *
   * Deliberately *not* the reader's action history. The reader subscribes to the bus directly, so
   * it records every action a child makes behind the parent pause overlay — actions the runtime
   * has already declared it is ignoring, because a pause is not a moment in which a child can
   * answer. Counting from it meant three presses behind the overlay plus one afterwards read as
   * four and awarded a correct answer (R1-7). A matcher only ever sees what the running mission
   * forwards to it, so counting here is what makes pause semantics true for count mechanics.
   */
  const observed = new Map<string, number>();

  return {
    mode: 'ACTION_COUNT',
    observe(event: ThriveInputEvent): MatcherVerdict {
      if (event.type !== onset) return { kind: 'IGNORED' };

      const soFar = (observed.get(event.playerId) ?? 0) + 1;
      observed.set(event.playerId, soFar);

      // Re-armed on every action: the answer is what the child has done when they stop, so
      // overshooting past the target is a wrong answer rather than an unreachable one.
      return {
        kind: 'ARMED',
        playerId: event.playerId,
        correct: soFar === action.count,
        commitAfterMs: options.tunables.countActionSettleMs,
      };
    },
    reset(playerId: PlayerId): void {
      observed.delete(playerId);
    },
    rollbackLastObservation(playerId: PlayerId): void {
      const soFar = observed.get(playerId);
      if (soFar === undefined) return;
      // Never below nothing: a rollback with no observation behind it is not a negative count.
      if (soFar <= 1) observed.delete(playerId);
      else observed.set(playerId, soFar - 1);
    },
  };
}

function createBinaryMatcher(action: ActionExpectation): ResponseMatcher {
  const expectedOnset = ONSET_EVENT_FOR_ACTION[action.kind];
  const opposite = BINARY_POSTURES.includes(action.kind)
    ? BINARY_POSTURES.filter((posture) => posture !== action.kind).map(
        (posture) => ONSET_EVENT_FOR_ACTION[posture],
      )
    : [];

  return {
    mode: 'SEMANTIC_ACTION',
    observe(event: ThriveInputEvent): MatcherVerdict {
      if (event.type === expectedOnset) {
        return { kind: 'RESOLVED', playerId: event.playerId, correct: true };
      }
      if (opposite.includes(event.type)) {
        // One child's wrong posture is one child's wrong answer. It no longer resolves the round
        // for the team — the runtime keeps listening for the other child (D-045 PS2-2(b)).
        return { kind: 'RESOLVED', playerId: event.playerId, correct: false };
      }
      // Retractions land here, and so does every action belonging to another mechanic. Neither
      // is an answer.
      return { kind: 'IGNORED' };
    },
    reset(): void {},
  };
}

export function createResponseMatcher(options: ResponseMatcherOptions): ResponseMatcher {
  const { expected } = options;

  if (!isSupportedResponseMode(expected)) {
    throw new UnsupportedResponseModeError(
      expected.mode,
      'the expected value names no answer this build can observe.',
    );
  }

  switch (expected.mode) {
    case 'BODY_ZONE': {
      const zone = zoneOf(expected.value);
      if (zone === null) break;
      return createZoneMatcher(zone, options.tunables.portalDwellMs);
    }
    case 'ACTION_COUNT': {
      const action = actionOf(expected.value);
      if (action?.count === undefined) break;
      return createCountMatcher({ kind: action.kind, count: action.count }, options);
    }
    case 'SEMANTIC_ACTION': {
      const action = actionOf(expected.value);
      if (action === null) break;
      // A zone expectation is a zone expectation however it is spelled, dwell included.
      if (action.kind === 'ENTER_ZONE' && action.zone !== undefined) {
        return createZoneMatcher(action.zone, options.tunables.portalDwellMs);
      }
      return createBinaryMatcher(action);
    }
    default:
      break;
  }

  throw new UnsupportedResponseModeError(expected.mode, 'no matcher is registered for it.');
}
